"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import type { StickToBottomContext } from "use-stick-to-bottom";
import {
    ArrowDown,
    ArrowUp,
    FileText,
    Loader2,
    MessageSquare,
    Square,
} from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import {
    Conversation,
    ConversationContent,
    ConversationBottomFade,
    ConversationScrollButton,
} from "~/components/ai-elements/conversation";
import { Message, MessageContent } from "~/components/ai-elements/message";
import {
    Reasoning,
    ReasoningContent,
    ReasoningTrigger,
} from "~/components/ai-elements/reasoning";
import {
    AssetChatResponse,
    getGenerationStatus,
    InterruptedNote,
    VerifyingCitationsNote,
} from "../AssetChat";
import * as Analytics from "../../analytics";
import { getCorpusAsset } from "../../assets";
import { ASSET_CHAT_API_PATH, ASSET_CHAT_SUGGESTIONS } from "../../consts";
import type {
    AssetChatCitation,
    AssetChatHistoryResponse,
    ChatUIMessage,
} from "../../types";
import { prettyDocTitle } from "./shared";

// // // //

/**
 * DockAssetChat
 *
 * The GlobalChatDock's chat surface — a design-matched sibling of the inline
 * {@link AssetChat}. Empty state renders the "Ask about this deal" intro, a 2×2
 * grid of starter questions and the composer (design story `deal`); once a
 * conversation starts it renders the transcript with grounded source chips and
 * a pinned composer (story `convo`). It owns no header chrome — the dock panel
 * frames it — and fills its flex parent so the transcript scrolls while the
 * composer stays put.
 *
 * Answers are grounded server-side in the asset's dataroom. On mount it hydrates
 * the user's latest conversation for the asset via `GET {ASSET_CHAT_API_PATH}`;
 * `startFresh` skips hydration and begins empty (nothing persists until the
 * first submitted message).
 */
export function DockAssetChat(props: {
    /** Corpus asset id (screener symbol), e.g. "ACRED" — sent to the API as `assetId`. */
    corpusAssetId: string;
    /** Short display label used in the copy, e.g. the asset symbol ("ACRED"). */
    assetName: string;
    /** Skip hydration and start an empty conversation. */
    startFresh?: boolean;
    className?: string;
}) {
    const { corpusAssetId, assetName, startFresh = false, className } = props;
    const [session, setSession] = useState<HydratedSession | null>(null);

    useEffect(() => {
        let cancelled = false;

        const hydrate = async () => {
            try {
                const response = await fetch(
                    `${ASSET_CHAT_API_PATH}?assetId=${encodeURIComponent(corpusAssetId)}`,
                    { headers: { accept: "application/json" } },
                );
                if (!response.ok) {
                    if (!cancelled) setSession(createFreshSession());
                    return;
                }
                const data =
                    (await response.json()) as AssetChatHistoryResponse;
                if (cancelled) return;
                setSession({
                    conversationId: data.conversationId ?? crypto.randomUUID(),
                    initialMessages: data.messages ?? [],
                });
            } catch {
                if (!cancelled) setSession(createFreshSession());
            }
        };

        if (startFresh) {
            setSession(createFreshSession());
        } else {
            setSession(null);
            void hydrate();
        }
        return () => {
            cancelled = true;
        };
    }, [corpusAssetId, startFresh]);

    return (
        <div className={cn("flex min-h-0 flex-col", className)}>
            {session ? (
                <DockChatSession
                    key={session.conversationId}
                    corpusAssetId={corpusAssetId}
                    assetName={assetName}
                    conversationId={session.conversationId}
                    initialMessages={session.initialMessages}
                />
            ) : (
                <div className="grid flex-1 place-items-center py-12 text-tertiary-foreground">
                    <Loader2
                        className="size-5 animate-spin"
                        aria-hidden="true"
                    />
                </div>
            )}
        </div>
    );
}

// // // //

type HydratedSession = {
    conversationId: string;
    initialMessages: ChatUIMessage[];
};

function createFreshSession(): HydratedSession {
    return {
        conversationId: crypto.randomUUID(),
        initialMessages: [],
    };
}

function messageText(message: ChatUIMessage): string {
    return message.parts
        .filter((part) => part.type === "text")
        .map((part) => (part.type === "text" ? part.text : ""))
        .join("");
}

function getMessageCitations(message: ChatUIMessage): AssetChatCitation[] {
    return message.parts.flatMap((part) =>
        part.type === "data-citation" ? [part.data] : [],
    );
}

function getMessageReasoning(message: ChatUIMessage): string {
    return message.parts
        .filter((part) => part.type === "reasoning")
        .map((part) => (part.type === "reasoning" ? part.text : ""))
        .join("\n\n")
        .trim();
}

/** Whether the assistant has emitted any visible reasoning or answer text yet. */
function hasStreamedContent(message: ChatUIMessage): boolean {
    return message.parts.some(
        (part) =>
            (part.type === "text" || part.type === "reasoning") &&
            part.text.trim().length > 0,
    );
}

// // // //

function DockChatSession(props: {
    corpusAssetId: string;
    assetName: string;
    conversationId: string;
    initialMessages: ChatUIMessage[];
}) {
    const { corpusAssetId, assetName, conversationId, initialMessages } = props;
    const [input, setInput] = useState("");
    // Whether the user stopped the in-flight generation (marks the partial).
    const [wasStopped, setWasStopped] = useState(false);
    const conversationContextRef = useRef<StickToBottomContext | null>(null);

    const transport = useMemo(
        () =>
            new DefaultChatTransport({
                api: ASSET_CHAT_API_PATH,
                body: { assetId: corpusAssetId },
            }),
        [corpusAssetId],
    );

    const { messages, sendMessage, status, regenerate, stop } =
        useChat<ChatUIMessage>({
            id: conversationId,
            messages: initialMessages,
            transport,
            // Batch stream updates into word-group chunks (~6-7/s) that each
            // fade in (see STREAMING_TEXT_ANIMATION in AssetChat), instead of
            // re-rendering on every token. Also caps markdown re-parses.
            experimental_throttle: 150,
        });

    const isBusy = status === "submitted" || status === "streaming";
    const hasMessages = messages.length > 0;
    const isError = status === "error";
    const lastMessage = messages[messages.length - 1];
    // Show the pending "Thinking…" indicator only until the assistant emits its
    // first reasoning or text — after that the message renders its own Reasoning
    // block and answer.
    const isWaitingForReply =
        isBusy &&
        (lastMessage?.role === "user" ||
            (lastMessage?.role === "assistant" &&
                !hasStreamedContent(lastMessage)));
    // The last answer is a partial: stopped by the user or cut off by an error.
    const interruptedMessageId =
        !isBusy &&
        (wasStopped || isError) &&
        lastMessage?.role === "assistant" &&
        hasStreamedContent(lastMessage)
            ? lastMessage.id
            : null;

    const scrollTranscriptToBottom = useCallback(() => {
        const scrollElement = conversationContextRef.current?.scrollRef.current;
        if (scrollElement) {
            scrollElement.scrollTo({
                top: scrollElement.scrollHeight,
                behavior: "instant",
            });
            return;
        }
        void conversationContextRef.current?.scrollToBottom("instant");
    }, []);

    const queueTranscriptScrollToBottom = useCallback(() => {
        requestAnimationFrame(() => {
            scrollTranscriptToBottom();
            requestAnimationFrame(() => scrollTranscriptToBottom());
        });
    }, [scrollTranscriptToBottom]);

    useEffect(() => {
        if (isBusy) {
            queueTranscriptScrollToBottom();
        }
    }, [isBusy, messages.length, queueTranscriptScrollToBottom]);

    const submit = (text: string, source: "typed" | "suggestion") => {
        const trimmed = text.trim();
        if (!trimmed || isBusy) {
            return;
        }
        Analytics.trackSendMessage({
            message: trimmed,
            assetCorpusId: corpusAssetId,
            conversationId,
            surface: "dock",
            source,
            isFirstMessage: messages.length === 0,
        });
        setWasStopped(false);
        void sendMessage({ text: trimmed });
        setInput("");
        queueTranscriptScrollToBottom();
    };

    const handleStop = () => {
        Analytics.trackStopGeneration({
            assetCorpusId: corpusAssetId,
            conversationId,
            surface: "dock",
        });
        setWasStopped(true);
        void stop();
    };

    // Track answers that end in the error banner.
    useEffect(() => {
        if (isError) {
            Analytics.trackResponseError({
                assetCorpusId: corpusAssetId,
                conversationId,
                surface: "dock",
            });
        }
    }, [isError, corpusAssetId, conversationId]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {hasMessages ? (
                <Conversation
                    contextRef={conversationContextRef}
                    className="min-h-0 flex-1"
                >
                    <ConversationContent
                        // Scrollbar rides the panel edge, invisible until
                        // hovered.
                        scrollClassName="overflow-y-auto [scrollbar-gutter:stable] [scrollbar-color:transparent_transparent] [transition:scrollbar-color_150ms_ease] hover:[scrollbar-color:var(--color-border)_transparent]"
                        // pb clears the h-12 ConversationBottomFade so streamed
                        // text never renders under the gradient.
                        className="gap-5 px-4 py-5 pb-14"
                    >
                        {messages.map((message, index) => (
                            <Message key={message.id} from={message.role}>
                                <MessageContent className="gap-3 group-[.is-user]:max-w-[80%] group-[.is-user]:bg-primary group-[.is-user]:px-3.5 group-[.is-user]:py-2.5 group-[.is-user]:text-white">
                                    {message.role === "assistant" ? (
                                        <AssistantMessage
                                            message={message}
                                            isActive={
                                                isBusy &&
                                                index === messages.length - 1
                                            }
                                            isInterrupted={
                                                message.id ===
                                                interruptedMessageId
                                            }
                                        />
                                    ) : (
                                        <span className="whitespace-pre-wrap text-sm leading-6">
                                            {messageText(message)}
                                        </span>
                                    )}
                                </MessageContent>
                            </Message>
                        ))}
                        {isWaitingForReply && <ThinkingIndicator />}
                    </ConversationContent>
                    <ConversationBottomFade />
                    {/* "Latest" pill: jump back to the newest message. */}
                    <ConversationScrollButton
                        size="sm"
                        className="bottom-3 h-7 gap-1 border-muted-foreground/60 bg-card px-3 text-xs font-medium shadow-md"
                    >
                        <ArrowDown className="size-3.5" aria-hidden="true" />
                        Latest
                    </ConversationScrollButton>
                </Conversation>
            ) : (
                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                    <DealIntro assetName={assetName} />
                    <div className="mt-4 grid grid-cols-2 gap-2">
                        {ASSET_CHAT_SUGGESTIONS.map((question, index) => (
                            <button
                                key={question}
                                type="button"
                                onClick={() => submit(question, "suggestion")}
                                disabled={isBusy}
                                // Staggered rise-in on first render for a subtle
                                // dynamic feel.
                                style={{
                                    animationDelay: `${index * 60}ms`,
                                }}
                                className="flex animate-suggestion-rise items-center gap-2 rounded-xl border border-border bg-muted/30 p-3 text-left transition hover:border-primary-light hover:bg-primary-light/30 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:animate-none"
                            >
                                <MessageSquare
                                    className="size-3.5 shrink-0 text-primary"
                                    aria-hidden="true"
                                />
                                <span className="text-xs leading-tight font-medium text-secondary-foreground">
                                    {question}
                                </span>
                            </button>
                        ))}
                    </div>
                    <UnderlyingDocuments corpusAssetId={corpusAssetId} />
                </div>
            )}

            {isError && (
                <div className="mx-4 mb-3 flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-sm text-destructive">
                    <span>Something went wrong. Please try again.</span>
                    <button
                        type="button"
                        onClick={() => void regenerate()}
                        className="shrink-0 font-medium underline underline-offset-2 hover:opacity-80"
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Tight gutter pulls the composer box close to the panel edge:
                ~5px box inset, ~19px text inset. */}
            <div className="shrink-0 p-1">
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        submit(input, "typed");
                    }}
                    className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 py-1.5 pr-1.5 pl-3.5"
                >
                    <input
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                        placeholder={`Ask anything about ${assetName}…`}
                        className="min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-foreground shadow-none placeholder:text-muted-foreground focus:border-0 focus:ring-0 focus:outline-none"
                        aria-label={`Ask anything about ${assetName}`}
                    />
                    {isBusy ? (
                        <Button
                            type="button"
                            size="icon"
                            onClick={handleStop}
                            aria-label="Stop generating"
                            title="Stop generating"
                            className="size-8 shrink-0 rounded-lg shadow-sm shadow-primary/25 hover:bg-primary-dark"
                        >
                            <Square
                                className="size-3 fill-current"
                                aria-hidden="true"
                            />
                        </Button>
                    ) : (
                        <Button
                            type="submit"
                            size="icon"
                            disabled={!input.trim()}
                            aria-label="Send"
                            className="size-8 shrink-0 rounded-lg shadow-sm shadow-primary/25 hover:bg-primary-dark disabled:shadow-none"
                        >
                            <ArrowUp aria-hidden="true" />
                        </Button>
                    )}
                </form>
            </div>
        </div>
    );
}

// // // //

/** Empty-state intro shown above the starter grid (design story `deal`). */
function DealIntro(props: { assetName: string }) {
    return (
        <div className="min-w-0">
            <h3 className="min-w-0 text-[15px] font-semibold text-foreground">
                Ask about this deal
            </h3>
            <p className="mt-1 text-xs leading-snug text-tertiary-foreground">
                Answers are drawn only from {props.assetName}&apos;s documents.
                This AI does not use the internet.
            </p>
        </div>
    );
}

/**
 * Assistant answer: the model's collapsible reasoning, then grounded prose with
 * inline `[n]` citation markers (shared with the inline {@link AssetChat} via
 * {@link AssetChatResponse}) plus a row of deduplicated source chips beneath.
 *
 * `isActive` is true only for the message currently streaming; combined with the
 * last part being reasoning, it drives the Reasoning block's live state.
 */
function AssistantMessage(props: {
    message: ChatUIMessage;
    isActive: boolean;
    isInterrupted: boolean;
}) {
    const { message, isActive, isInterrupted } = props;
    const reasoning = getMessageReasoning(message);
    const text = messageText(message);
    const citations = getMessageCitations(message);
    const isReasoningStreaming =
        isActive && message.parts.at(-1)?.type === "reasoning";
    const isVerifyingCitations =
        isActive &&
        getGenerationStatus(message)?.state === "verifying-citations";

    return (
        <>
            {reasoning.length > 0 && (
                <Reasoning
                    isStreaming={isReasoningStreaming}
                    defaultOpen={isReasoningStreaming}
                >
                    <ReasoningTrigger />
                    <ReasoningContent>{reasoning}</ReasoningContent>
                </Reasoning>
            )}
            {text.length > 0 && (
                <AssetChatResponse
                    text={text}
                    citations={citations}
                    isStreaming={isActive}
                />
            )}
            {isVerifyingCitations && <VerifyingCitationsNote />}
            {isInterrupted && <InterruptedNote />}
            <SourceChips citations={citations} />
        </>
    );
}

/**
 * Single grounded-document chip. Shared between the source rows under assistant
 * answers and the empty state's "Grounded in [N] documents" list.
 */
function SourceChip(props: {
    title: string;
    className?: string;
    labelClassName?: string;
}) {
    return (
        <span
            className={cn(
                "inline-flex h-[26px] items-center gap-1.5 rounded-md border border-muted bg-muted/30 px-2 text-[11px] text-tertiary-foreground",
                props.className,
            )}
            title={prettyDocTitle(props.title)}
        >
            <FileText
                className="size-3 shrink-0 text-primary"
                aria-hidden="true"
            />
            <span className={cn("truncate", props.labelClassName)}>
                {prettyDocTitle(props.title)}
            </span>
        </span>
    );
}

/** Row of grounded document chips rendered under an assistant answer. */
function SourceChips(props: { citations: AssetChatCitation[] }) {
    const seen = new Set<string>();
    const unique = props.citations.filter((citation) => {
        if (seen.has(citation.title)) {
            return false;
        }
        seen.add(citation.title);
        return true;
    });

    if (unique.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-wrap gap-1.5">
            {unique.map((citation) => (
                <SourceChip
                    key={citation.title}
                    title={citation.title}
                    labelClassName="max-w-[15rem]"
                />
            ))}
        </div>
    );
}

/**
 * Empty-state section listing the asset's underlying data-room documents —
 * a "Grounded in [N] documents" divider bar, then one citation chip per doc.
 */
function UnderlyingDocuments(props: { corpusAssetId: string }) {
    const documentNames =
        getCorpusAsset(props.corpusAssetId)?.documentNames ?? [];

    if (documentNames.length === 0) {
        return null;
    }

    return (
        <div
            // Rises in after the starter suggestions finish their stagger.
            style={{
                animationDelay: `${ASSET_CHAT_SUGGESTIONS.length * 60}ms`,
            }}
            className="mt-5 animate-suggestion-rise motion-reduce:animate-none"
        >
            <div className="flex items-center gap-2">
                <h4 className="shrink-0 text-[11px] font-medium tracking-wide text-tertiary-foreground uppercase">
                    Grounded in {documentNames.length}{" "}
                    {documentNames.length === 1 ? "document" : "documents"}
                </h4>
                <div aria-hidden="true" className="h-px flex-1 bg-border" />
            </div>
            <ul className="mt-2.5 flex flex-col gap-1.5">
                {documentNames.map((name) => (
                    <li key={name} className="flex min-w-0">
                        <SourceChip
                            title={name}
                            className="w-full"
                            labelClassName="min-w-0 flex-1"
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * In-flight assistant placeholder shown until the first reasoning/answer token
 * arrives. Reuses the Reasoning trigger's streaming look so it flows straight
 * into the live reasoning block once tokens start.
 */
function ThinkingIndicator() {
    return (
        <Message from="assistant">
            <MessageContent role="status" aria-live="polite">
                <Reasoning isStreaming>
                    <ReasoningTrigger />
                </Reasoning>
            </MessageContent>
        </Message>
    );
}
